import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { useAuthedApi, errMsg } from "../../lib/api";
import ImageUploader from "../ImageUploader";

export default function BannerForm({ banner, loc, nextOrder, onClose, onSaved }) {
  const api = useAuthedApi();
  const isNew = !banner.id;
  const [f, setF] = useState({
    image_url: banner.image_url || "", title: banner.title || "", link: banner.link || "",
    order: banner.order ?? nextOrder, active: banner.active ?? true, start_at: banner.start_at || "", end_at: banner.end_at || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!f.image_url) return toast.error("Upload a banner image");
    if (f.link && !/^(\/\S*|https:\/\/\S+)$/.test(f.link)) return toast.error("Link must start with / or https://");
    if (f.start_at && f.end_at && f.end_at < f.start_at) return toast.error("End date must be after start date");
    setBusy(true);
    const body = { ...f, state: banner.state || loc.state, city: banner.city || loc.city, order: Number(f.order) || 0, start_at: f.start_at || null, end_at: f.end_at || null };
    try {
      await (isNew ? api("post", "/admin/banners", body) : api("put", `/admin/banners/${banner.id}`, body));
      toast.success(isNew ? "Banner added" : "Banner updated");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111319] border-[#D4AF37]/30 text-white sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="banner-form">
        <DialogHeader><DialogTitle className="font-display text-3xl gold-text">{isNew ? "New banner" : "Edit banner"} · {(banner.city || loc.city) === "__all__" ? `All cities in ${banner.state || loc.state}` : banner.city || loc.city}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="lux-label">Banner image (JPG/PNG/WEBP, max 8 MB · best 1600×500)</label>
            <div className="w-80"><ImageUploader value={f.image_url ? [f.image_url] : []} onChange={(fn) => setF((p) => ({ ...p, image_url: fn(p.image_url ? [p.image_url] : []).slice(-1)[0] || "" }))} max={1} testid="banner-image" /></div>
          </div>
          <div><label className="lux-label">Title (optional)</label><input className="lux-input" maxLength={120} value={f.title} onChange={set("title")} data-testid="banner-title" /></div>
          <div><label className="lux-label">Link (optional, e.g. /cars?brand=BMW or https://…)</label><input className="lux-input" maxLength={300} value={f.link} onChange={set("link")} data-testid="banner-link" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="lux-label">Order</label><input className="lux-input" type="number" min="0" max="999" value={f.order} onChange={set("order")} data-testid="banner-order" /></div>
            <div><label className="lux-label">Start date</label><input className="lux-input" type="date" value={f.start_at} onChange={set("start_at")} data-testid="banner-start" /></div>
            <div><label className="lux-label">End date</label><input className="lux-input" type="date" value={f.end_at} onChange={set("end_at")} data-testid="banner-end" /></div>
          </div>
          <label className="flex items-center gap-3 text-sm cursor-pointer"><Switch className="data-[state=unchecked]:bg-gray-600" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="banner-form-active" />Active</label>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn-gold" disabled={busy} data-testid="banner-save">{busy ? "Saving..." : "Save banner"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
